import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { errMsg } from '@/lib/api';
import { Fingerprint, X } from 'lucide-react';
import api from '@/lib/api';
import {
  isMobile, isQuickLoginAvailable, enrollQuickLogin, setQuickHint,
} from '@/lib/webauthn';

const promptedKey = (u) => `sc_quick_prompted_${u}`;
const defaultDeviceLabel = () => {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iPhone';
  if (/Android/i.test(ua)) return 'Android phone';
  return 'My phone';
};

// One-time, mobile-only prompt that invites an allowed user to enable biometric
// Quick Login right after they sign in. Enrollment is ALSO always available from
// Settings; this gate only auto-asks once per user (until they enroll).
export const BiometricEnrollGate = () => {
  const { user } = useApp();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const checkedFor = useRef(null);

  useEffect(() => {
    const u = user?.username;
    if (!u) { checkedFor.current = null; return; }
    if (user.role === 'PARTNER') return;
    if (!user.allow_biometric) return;
    if (!isMobile()) return;
    if (checkedFor.current === u) return;         // already evaluated this session
    checkedFor.current = u;
    if (localStorage.getItem(promptedKey(u)) === '1') return;  // asked/enrolled before
    (async () => {
      if (!(await isQuickLoginAvailable())) return;
      try {
        const r = await api.get('/auth/webauthn/credentials');
        const creds = r.data?.data?.credentials || [];
        if (creds.length === 0) setOpen(true);     // no device yet -> invite once
        else localStorage.setItem(promptedKey(u), '1');
      } catch (e) { /* ignore — availability best-effort */ }
    })();
  }, [user]);

  const dismiss = () => {
    if (user?.username) localStorage.setItem(promptedKey(user.username), '1');
    setOpen(false);
  };

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await enrollQuickLogin(defaultDeviceLabel());
      setQuickHint(user);
      if (user?.username) localStorage.setItem(promptedKey(user.username), '1');
      toast.success('Quick Login is on. Next time, sign in with Face ID / fingerprint.');
      setOpen(false);
    } catch (err) {
      const name = err?.name;
      if (name === 'NotAllowedError' || name === 'AbortError') toast.error('Setup was cancelled.');
      else toast.error(errMsg(err) || 'Could not enable Quick Login on this device.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DrawerContent className="bg-card" data-testid="biometric-enroll-sheet">
        <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
        <div className="px-6 pb-8 pt-2 text-center">
          <button onClick={dismiss} aria-label="Close"
            className="absolute right-4 top-4 rounded-full p-1.5 text-muted-foreground hover:bg-secondary" data-testid="biometric-enroll-close">
            <X className="h-4 w-4" />
          </button>
          <span className="sc-fp-wrap mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
            {busy && <span className="sc-fp-ring" />}
            <Fingerprint className={`sc-fp-icon h-11 w-11 text-primary ${busy ? 'sc-fp-busy' : ''}`} />
          </span>
          <h2 className="font-display text-xl font-bold tracking-tight">Enable Quick Login?</h2>
          <p className="mx-auto mt-2 max-w-xs text-sm text-muted-foreground">
            Sign in faster next time with your phone's Face ID, Touch ID or fingerprint —
            no password to type. Your biometrics never leave your device.
          </p>
          <div className="mt-6 space-y-2">
            <Button onClick={enable} disabled={busy} className="h-12 w-full rounded-xl text-base font-semibold" data-testid="biometric-enroll-enable">
              {busy ? 'Setting up…' : 'Enable Quick Login'}
            </Button>
            <Button variant="ghost" onClick={dismiss} disabled={busy} className="h-11 w-full rounded-xl" data-testid="biometric-enroll-later">
              Not now
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
